import { Button, Typography } from "theme";
import { useParams } from "react-router-dom";
import { FC, useCallback, useState } from "react";
import { useBookingPayment } from "../../../../api/hooks/useBookingPayment/useBookingPayment.ts";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { paymentScheme, PaymentFormScheme } from "./validation.ts";
import { Box, CircularProgress, Dialog, DialogTitle, Grid2 } from "@mui/material";
import ControlledTextField from "../../../ControlledInputs/ControlledTextField.tsx";
import { InvertButton } from "../../../../theme/components/Button/Button.tsx";
import ControlledSelect from "../../../ControlledInputs/ControlledSelect.tsx";
import usePaymentMethods from "../../../../api/hooks/usePaymentMethods/usePaymentMethods.tsx";
import { PaymentMethodsConfig } from "../../../../api/hooks/usePaymentMethods/type.ts";

type PaymentProps = {
  amount: number;
}

const Payment:FC<PaymentProps> = ({amount}) => {
  const { id } = useParams();
  const [isEditPriceModalOpen, setIsEditPriceModalOpen] = useState(false);

  const { paymentMethods, isPending } = usePaymentMethods();
  const makePaymentProvider = useBookingPayment(id || "");

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<PaymentFormScheme>({
    reValidateMode: "onSubmit",
    resolver: zodResolver(paymentScheme),
    defaultValues: {
      amount: String(amount),
      paymentMethod: "",
    },
  });

  const onSubmit = useCallback(async (data: PaymentFormScheme) => {
    try {
      await makePaymentProvider.mutateAsync(data);
      reset();
      setIsEditPriceModalOpen(false);
    } catch (e) {
      console.error(e);
    }
  }, [makePaymentProvider, reset]);

  if (isPending) {
    return <Grid2 mt={2} container justifyContent="center">
      <CircularProgress color="secondary" />
    </Grid2>;
  }

  return (
    <div>
      <Button type='button' fullWidth look="normal"  onClick={() => setIsEditPriceModalOpen(true)}>Оплатити</Button>
      <Dialog sx={{
        "& .MuiPaper-root": {
          backgroundColor: "white",
          padding: "16px",
          minWidth: "300px",
        },
      }} open={isEditPriceModalOpen} onClose={() => setIsEditPriceModalOpen(false)}>
        <DialogTitle>Оплата</DialogTitle>

        <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate my={3.5}>
          <Box mb={2}>
            <ControlledTextField
              control={control}
              required
              name="amount"
              label="Сума"
              placeholder="Введіть суму"
              type="number"
            />
            {errors.amount?.message &&
              <Typography color="error" variant="body2">{errors.amount.message}</Typography>}
          </Box>

          <Box mb={2}>
            <ControlledSelect error={!!errors.paymentMethod?.message} name='paymentMethod' control={control} options={paymentMethods?.map(el => ({
              label: PaymentMethodsConfig[el],
              value: el,
            })) || []} />
          </Box>

          <InvertButton fullWidth type="submit" disabled={isSubmitting} loading={isSubmitting}>
            Оплатити
          </InvertButton>
        </Box>
      </Dialog>
    </div>
  );
};

export default Payment;
